<template>
  <ValidationObserver ref="form">
    <form @submit.prevent="validate">
      <div class="md-layout text-center">
        <div class="md-layout-item md-size-35 md-small-size-60">
          <ValidationProvider
            name="cep"
            :rules="{
              required: true, 
              regex: /^\d{5}-\d{3}$/ 
            }"
            v-slot="{ passed, failed }"
          >
            <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
              <md-icon>room</md-icon>
              <label>Qual seu CEP?</label>
              <md-input v-model="CEP" v-mask="'#####-###'" autocomplete="CEP" type="text" @keyup="enterKey"></md-input>

              <slide-y-down-transition>
                <md-icon class="error" v-show="failed">close</md-icon>
              </slide-y-down-transition>
              <slide-y-down-transition>
                <md-icon class="success" v-show="passed">done</md-icon>
              </slide-y-down-transition>
            </md-field>
          </ValidationProvider>
        </div>
      </div>

      <CollapseContent
        :active="!addressLoaded">
        <template slot="md-collapse-content">
          <div class="additional-info-content md-layout-item md-layout-item md-size-100 mx-auto text-center">
            <p class="additional-info">
              De acordo com o Decreto Federal nº 7962/13, todas as lojas virtuais precisam apresentar o endereço físico no rodapé, mesmo que esta seja sua residência. Isso traz maior segurança e credibilidade aos seus clientes.
            </p>
            <p class="additional-info">
              Caso precise mudar essa informação depois, você poderá fazer pelo seu painel de controle.
            </p>
          </div>
        </template>              
      </CollapseContent>

      <CollapseContent
        :active="addressLoaded">
        <template slot="md-collapse-content">

          <div class="md-layout text-center">
            <div class="md-layout-item md-size-70">
              <ValidationProvider
                name="street"
                :rules="{
                  required: addressLoaded, 
                  min: 3
                }"
                v-slot="{ passed, failed }"
              >
                <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                  <label>Rua / Avenida</label>
                  <md-input v-model="street" autocomplete="streetName" type="text"> </md-input>

                  <slide-y-down-transition>
                    <md-icon class="error" v-show="failed">close</md-icon>
                  </slide-y-down-transition>
                  <slide-y-down-transition>
                    <md-icon class="success" v-show="passed">done</md-icon>
                  </slide-y-down-transition>
                </md-field>
              </ValidationProvider>
            </div>

            <div class="md-layout-item md-size-30">
              <ValidationProvider 
                name="number"
                :rules="{required: addressLoaded, min: 1}"
                v-slot="{ passed, failed }"
              >
                <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                  <label>Número</label>
                  <md-input v-model="number" class="address-small" autocomplete="streetNumber" type="text"> </md-input>

                  <slide-y-down-transition>
                    <md-icon class="error" v-show="failed">close</md-icon>
                  </slide-y-down-transition>
                  <slide-y-down-transition>
                    <md-icon class="success" v-show="passed">done</md-icon>
                  </slide-y-down-transition>
                </md-field>
              </ValidationProvider>
            </div>

            <div class="md-layout-item md-size-40">
              <ValidationProvider
                name="complement"            
                v-slot="{ passed, failed }"
              >
                <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                  <label>Complemento</label>
                  <md-input v-model="complement" autocomplete="streetComplement" type="text"> </md-input>

                  <slide-y-down-transition>
                    <md-icon class="error" v-show="failed">close</md-icon>
                  </slide-y-down-transition>
                  <slide-y-down-transition>
                    <md-icon class="success" v-show="passed">done</md-icon>
                  </slide-y-down-transition>
                </md-field>
              </ValidationProvider>
            </div>
            <div class="md-layout-item md-size-60">
              <ValidationProvider 
                name="neighborhood"
                :rules="{
                  required: addressLoaded, 
                  min: 3
                }"
                v-slot="{ passed, failed }"
              >
                <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                  <label>Bairro</label>
                  <md-input v-model="neighborhood" autocomplete="neighborhood" type="text"> </md-input>

                  <slide-y-down-transition>
                    <md-icon class="error" v-show="failed">close</md-icon>
                  </slide-y-down-transition>
                  <slide-y-down-transition>
                    <md-icon class="success" v-show="passed">done</md-icon>
                  </slide-y-down-transition>
                </md-field>
              </ValidationProvider>
            </div>

            <div class="md-layout-item md-size-70">
              <ValidationProvider 
                name="city"
                :rules="{
                  required: addressLoaded, 
                  min: 3
                }"
                v-slot="{ passed, failed }"
              >
                <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                  <label>Cidade</label>
                  <md-input v-model="city" autocomplete="city" type="text"> </md-input>

                  <slide-y-down-transition>
                    <md-icon class="error" v-show="failed">close</md-icon>
                  </slide-y-down-transition>
                  <slide-y-down-transition>
                    <md-icon class="success" v-show="passed">done</md-icon>
                  </slide-y-down-transition>
                </md-field>
              </ValidationProvider>
            </div>
            <div class="md-layout-item md-size-30">
              <ValidationProvider 
                name="state"
                :rules="{required: addressLoaded }"
                v-slot="{ passed, failed }"
              >
                <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                  <label for="state">Estado</label>
                  <md-select v-model="state" autocomplete="state" name="state">
                    <md-option value="AC">AC</md-option>
                    <md-option value="AL">AL</md-option>
                    <md-option value="AP">AP</md-option>
                    <md-option value="AM">AM</md-option>
                    <md-option value="BA">BA</md-option>
                    <md-option value="CE">CE</md-option>
                    <md-option value="DF">DF</md-option>
                    <md-option value="ES">ES</md-option>
                    <md-option value="GO">GO</md-option>
                    <md-option value="MA">MA</md-option>
                    <md-option value="MT">MT</md-option>
                    <md-option value="MS">MS</md-option>
                    <md-option value="MG">MG</md-option>
                    <md-option value="PA">PA</md-option>
                    <md-option value="PB">PB</md-option>
                    <md-option value="PR">PR</md-option>
                    <md-option value="PE">PE</md-option>
                    <md-option value="PI">PI</md-option>
                    <md-option value="RJ">RJ</md-option>
                    <md-option value="RN">RN</md-option>
                    <md-option value="RS">RS</md-option>
                    <md-option value="RO">RO</md-option>
                    <md-option value="RR">RR</md-option>
                    <md-option value="SC">SC</md-option>
                    <md-option value="SP">SP</md-option>
                    <md-option value="SE">SE</md-option>
                    <md-option value="TO">TO</md-option>
                  </md-select>
                  <slide-y-down-transition>
                    <md-icon class="error" v-show="failed">close</md-icon>
                  </slide-y-down-transition>
                  <slide-y-down-transition>
                    <md-icon class="success" v-show="passed">done</md-icon>
                  </slide-y-down-transition>
                </md-field>       
              </ValidationProvider>       
            </div>
          </div>

        </template>
      </CollapseContent>

    </form>
  </ValidationObserver>
</template>
<script>
import { SlideYDownTransition } from "vue2-transitions";
import { extend } from "vee-validate";
import { CollapseContent } from '@/components'
import { regex, required, is, min } from "vee-validate/dist/rules";
extend("required", required);
extend("regex", regex);
extend("is", is);
extend("min", min);

export default {
  components: {
    SlideYDownTransition,
    CollapseContent
  },
  data() {
    return {
      address: this.$store.state.address,

      CEP: this.$store.state.address.CEP,
      street: null,
      number: null,
      complement: null,
      neighborhood: null,
      city: null,
      cityCode: null,
      state: null,
      stateCode: null,
      country: "BR",
      addressLoaded: false,
    };
  },

  mounted: function () {
    this.$nextTick(function () {
      this.$debug('loading ThirdStep:');
      this.$debug(this.address);

      if (this.address.CEP) this.addressLoaded = true;
      if (this.address.street) this.street = this.address.street;
      if (this.address.number) this.number = this.address.number;
      if (this.address.complement) this.complement = this.address.complement;
      if (this.address.neighborhood) this.neighborhood = this.address.neighborhood;
      if (this.address.city) this.city = this.address.city;
      if (this.address.cityCode) this.cityCode = this.address.cityCode;
      if (this.address.state) this.state = this.address.state;
      if (this.address.stateCode) this.stateCode = this.address.stateCode;
      if (this.address.country) this.country = this.address.country;
    })
  },

  watch: {
    CEP: function(val){
      if (this.addressLoaded){
        this.street       = "";
        this.number       = "";
        this.complement   = "";
        this.neighborhood = "";        
        this.city         = "";
        this.cityCode     = "";
        this.state        = "";
        this.stateCode    = "";
        this.addressLoaded = false;
      }
    }
  },

  methods: {
    enterKey: function(e) {      
      if (e && e.keyCode === 13) {        
        if (!this.addressLoaded) this.validate();
      }      
    },

    async validate() {      
      if (this.addressLoaded){
        this.$debug('ThirdStep => Validating Address');

        return this.$refs.form.validate().then(res => {
          this.$emit("on-validated", res);
          return res;
        });
      } else {
        this.$debug('ThirdStep => Validating CEP');
        
        return await this.$refs.form.$children[0].validate().then(async (res) => {          
          if (res.valid)
            await this.loadAddress();
        });
      }
    },

    async loadAddress(){
      this.$debug('ThirdStep => loadAddress');
      this.$store.commit('loader', [true, "Aguarde", "Verificando endereço..."]);
      this.addressLoaded = false;

      const response = await this.$functions.getAddress(this.CEP);      
      if (response.status == 'success') {
        this.street       = response.address.street;
        this.neighborhood = response.address.neighborhood;
        this.city         = response.address.city;
        this.cityCode     = response.address.cod_ibge;
        this.state        = response.address.uf;
        this.stateCode    = response.address.cod_ibge_state;
      }

      this.addressLoaded = true;
      this.$store.commit('loader', []);
    },

  }
};
</script>
<style lang="scss" scoped>
.addres-small {
  max-width: 70px;
}

.additional-info-content{
  margin-top: 20px;
}

.additional-info{
  font-size: 14px;
  line-height: 20px;
  color: #555;
}

</style>


//FIXED BAD INPUT ERROR
//https://github.com/vuematerial/vue-material/pull/2287/files